import { useCallback, useEffect, useId, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import NotFoundPage from "components/error/NotFoundPage";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import { getSearchQuery } from "components/SearchInput/helpers";
import useErrorHandle from "hooks/useErrorHandle";
import useURLParams from "hooks/useURLParams";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import TablePagination from "components/Table/Pagination";
import usePagination from "components/Table/Pagination/usePagination";
import { PAGINATION_OPTIONS } from "components/Table/Pagination/constants";
import { AccountContext } from "views/AccountWrapper";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Button from "ds/components/Button";
import useDeployments from "views/Account/Template/Deployments/hooks/useDeployments";
import DeploymentsTable from "views/Account/Template/Deployments/Table";
import TemplateDeploymentEmpty from "views/Account/Template/Deployments/Empty";
import TemplateDeploymentsFiltersLayout from "views/Account/Template/Deployments/FiltersLayout";
import { BlueprintState, BlueprintVersionedGroup } from "types/generated";
import TemplateFeatureGateTooltip from "views/Account/TemplatesWorkbench/FeatureGate/Tooltip";
import { TemplateActions } from "views/Account/TemplatesWorkbench/FeatureGate/types";

import { DEPLOYMENTS_LIMIT_STORAGE_KEY, DEPLOYMENTS_LIMIT } from "./constants";

type TemplateVersionDeploymentsProps = {
  templateId: string;
  versionId: string;
  template: BlueprintVersionedGroup;
  versionState: BlueprintState;
};

const TemplateVersionDeployments = ({
  templateId,
  versionId,
  template,
  versionState,
}: TemplateVersionDeploymentsProps) => {
  const { accountName } = useTypedContext(AccountContext);
  const navigate = useNavigate();
  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);
  const [itemsCount, setItemsCount] = useState<number>(0);
  const [searchParams] = useSearchParams();

  useTitle(`Template Version Deployments · ${accountName}`);

  const {
    initialLoading,
    error,
    deployments,
    loadDeployments,
    endCursor,
    startCursor,
    noResults,
    isEmpty,
    loadingContent,
    predicates,
  } = useDeployments({ id: templateId, versionId });

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
    resetToInitialPage: resetPaginationToInitialPage,
  } = usePagination({
    fetchItems: loadDeployments,
    endCursor,
    startCursor,
    visibleItemsCount: deployments.length,
    initialLimit: DEPLOYMENTS_LIMIT,
    allItemsCount: itemsCount,
    storageKey: DEPLOYMENTS_LIMIT_STORAGE_KEY,
  });

  const handleSortCallback = useCallback(() => {
    resetPaginationToInitialPage();
  }, [resetPaginationToInitialPage]);

  useEffect(() => {
    if (searchParams.size === 0 && page !== 1) {
      goToFirstPage();
    }
  }, [searchParams, page, goToFirstPage]);

  const ErrorContent = useErrorHandle(error);

  const titleId = useId();

  const isPublished = versionState === BlueprintState.Published;

  const handleOpenDeployDrawer = () => {
    navigate(`/template/${templateId}/version/${versionId}/deploy`);
  };

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading) {
    return (
      <>
        <PageInfoHeader title="Deployments" />
        <PageLayoutSkeleton />
      </>
    );
  }

  if (noResults) {
    return <NotFoundPage />;
  }

  const hasNoResultsWithFiltering = searchInput.length > 0 || predicates.length > 0;

  return (
    <>
      <PageInfoHeader title="Deployments">
        {isPublished && (
          <TemplateFeatureGateTooltip
            action={TemplateActions.EditTemplate}
            template={template}
            on={({ isDisabled, ...props }) => (
              <Button
                {...props}
                variant="primary"
                size="medium"
                onPress={handleOpenDeployDrawer}
                disabled={isDisabled}
              >
                Deploy
              </Button>
            )}
          />
        )}
      </PageInfoHeader>

      <TemplateDeploymentsFiltersLayout
        predicates={predicates}
        itemsCount={itemsCount}
        setItemsCount={setItemsCount}
        titleId={titleId}
        id={templateId}
      >
        {isEmpty && <TemplateDeploymentEmpty hasNoResults={hasNoResultsWithFiltering} />}

        {deployments.length > 0 && (
          <>
            <DeploymentsTable
              deployments={deployments}
              loading={loadingContent}
              rowsPerPage={rowsPerPage}
              sortCallback={handleSortCallback}
            />

            <TablePagination
              from={from}
              to={to}
              loading={loadingContent}
              goToNextPage={goToNextPage}
              setRowsPerPage={setRowsPerPage}
              goToPrevPage={goToPrevPage}
              goToFirstPage={goToFirstPage}
              goToLastPage={goToLastPage}
              rowsPerPage={rowsPerPage}
              itemsCount={itemsCount}
              lastPage={lastPage}
              page={page}
              paginationOptions={PAGINATION_OPTIONS}
            />
          </>
        )}
      </TemplateDeploymentsFiltersLayout>
    </>
  );
};

export default TemplateVersionDeployments;
